import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AssigneeSelect } from "@/components/tasks/AssigneeSelect";
import { Printer, Plus, Trash2, CalendarDays, ListChecks } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString() : "—");

function Sec({ title, children, empty, testid }) {
  return (
    <section className="prep-sec mb-3" data-testid={testid}>
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-sky-800 border-b border-slate-300 pb-0.5 mb-1.5">{title}</h2>
      {children || <p className="text-xs text-slate-400">{empty || "No answers provided."}</p>}
    </section>
  );
}

const Rows = ({ rows }) => (rows?.length ? (
  <dl className="grid grid-cols-[minmax(140px,38%)_1fr] gap-x-3 gap-y-0.5 text-[12.5px]">
    {rows.map((r, i) => <React.Fragment key={i}><dt className="text-slate-500">{r.label}</dt><dd className="text-slate-900">{String(r.value)}</dd></React.Fragment>)}
  </dl>) : null);

const Bullets = ({ items }) => (items?.length ? <ul className="list-disc pl-4 text-[12.5px] space-y-0.5">{items.map((t, i) => <li key={i}>{t}</li>)}</ul> : null);

function Staff({ s }) {
  const lic = (p) => [p.pc && "P&C", p.lh && "L&H"].filter(Boolean).join(" + ");
  return (
    <div className="text-[12.5px] space-y-1">
      {s.current.length ? <table className="w-full"><tbody>{s.current.map((p, i) => (
        <tr key={i} className="border-b border-slate-100"><td className="py-0.5">{p.name}</td><td>{p.kind === "producer" ? "Contracted Producer" : "Agent Staff"}</td><td>{p.title || ""}</td>
          <td className="text-slate-500">{lic(p) ? `${lic(p)} (self-reported)` : ""}</td><td className="text-slate-500 capitalize">{p.status === "changes" ? "correction noted" : p.status}</td></tr>))}</tbody></table>
        : <p className="text-slate-400 text-xs">No staff or producers on file.</p>}
      {s.additions.length > 0 && <p><b>Proposed additions:</b> {s.additions.map((a) => `${a.name}${a.kind === "producer" ? " (Producer)" : " (Staff)"}${a.title ? ` — ${a.title}` : ""}`).join("; ")}</p>}
      {s.departures.length > 0 && <p><b>Reported departures:</b> {s.departures.join(", ")}</p>}
      {s.none_additional && <p className="text-slate-500">Agent reports no additional staff or producers.</p>}
      <p className="text-slate-500">Proposed record corrections — pending {s.corrections.pending}, approved {s.corrections.approved}, rejected {s.corrections.rejected}</p>
    </div>
  );
}

function Doc({ d }) {
  const o = d.overview;
  return (
    <article className="prep-doc bg-white text-slate-900 mx-auto max-w-[8.5in] p-8 print:p-0 shadow-sm print:shadow-none" data-testid="meeting-prep-doc">
      <header className="flex justify-between items-end border-b-2 border-sky-800 pb-2 mb-3">
        <div><p className="text-[11px] uppercase tracking-widest text-slate-500">Annual Agency Review · Meeting Prep</p>
          <h1 className="text-xl font-bold" data-testid="prep-agent-name">{o.agent_name}{o.agency_name ? ` — ${o.agency_name}` : ""}</h1></div>
        <p className="text-[11px] text-slate-500 text-right">Review {o.review_year} · Plan {o.planning_year}<br />Submitted {fmtDate(o.submitted_at)}{o.revision > 1 ? ` (rev ${o.revision})` : ""}</p>
      </header>
      <Sec title="Agency Overview" testid="prep-overview">
        <Rows rows={[{ label: "Agency code", value: o.agency_code || "—" }, { label: "Appointment date", value: fmtDate(o.appointment_date) },
          { label: "Reporting period", value: `Jan 1, ${o.review_year} – ${o.reporting_cutoff_date ? fmtDate(o.reporting_cutoff_date) : "cutoff not set"}` },
          { label: "Meeting date", value: o.meeting_date ? fmtDate(o.meeting_date) : "________________" },
          { label: "Agent Staff / Producers", value: `${o.staff_count} staff · ${o.producer_count} producers` }]} />
      </Sec>
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-x-6">
        <Sec title="Business Snapshot" testid="prep-snapshot"><Rows rows={[...d.snapshot, ...d.ratings.map((r) => ({ label: r.label, value: `${r.value} / 5` }))]} /></Sec>
        <Sec title="Goals & Priorities" testid="prep-goals"><Rows rows={[...d.goals, ...d.hiring]} /></Sec>
        <Sec title="Key Strengths" testid="prep-strengths"><Bullets items={d.strengths} /></Sec>
        <Sec title="Opportunities / Areas to Discuss" testid="prep-opps"><Bullets items={d.opportunities} /></Sec>
        <Sec title="Systems & Tools" testid="prep-systems"><Rows rows={d.systems} /></Sec>
        <Sec title="Training / Support Requests" testid="prep-training"><Rows rows={d.training} /></Sec>
      </div>
      <Sec title="Staffing" testid="prep-staff"><Staff s={d.staff} /></Sec>
      {d.notes.length > 0 && <Sec title="Agent's Topics & Comments" testid="prep-notes"><Rows rows={d.notes} /></Sec>}
      <Sec title="Follow-Up Items" testid="prep-followups">
        <Bullets items={d.follow_ups} />
        {d.follow_up_tasks?.length > 0 && (
          <table className="w-full text-[12.5px] mt-1" data-testid="prep-task-list"><tbody>{d.follow_up_tasks.map((t) => (
            <tr key={t.id} className="border-b border-slate-100"><td className="py-0.5 text-slate-500">{t.human_id}</td><td>{t.title}</td>
              <td>{t.assignee_name || "Unassigned"}</td><td>{t.due_date ? `Due ${fmtDate(t.due_date.slice(0, 10))}` : ""}</td><td className="text-slate-500 capitalize">{(t.status || "").replace(/_/g, " ")}</td></tr>))}</tbody></table>
        )}
        <div className="mt-1 space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="border-b border-slate-300 h-4" />)}</div>
      </Sec>
      <footer className="text-[10px] text-slate-400 mt-2">Generated from the submitted survey. Ratings and goals are agent-reported, not verified production results.{d.confidential_included ? " Contains confidential management information." : ""}</footer>
    </article>
  );
}

function MeetingDate({ d, reload }) {
  const [v, setV] = useState(d.overview.meeting_date || "");
  const save = async () => {
    try { await api.patch(`/form-assignments/${d.assignment_id}/meeting`, { meeting_date: v }); toast.success("Meeting date saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-600">Meeting date</span>
      <Input type="date" value={v} onChange={(e) => setV(e.target.value)} className="h-9 w-40 bg-white" data-testid="prep-meeting-date" />
      <Button size="sm" variant="outline" onClick={save} disabled={v === (d.overview.meeting_date || "")} data-testid="prep-meeting-date-save">Save</Button></div>
  );
}

function TaskRow({ r, i, personId, set, remove }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_150px_auto] gap-2 items-center" data-testid={`fu-row-${i}`}>
      <Input value={r.title} onChange={(e) => set({ title: e.target.value })} placeholder="Follow-up task" data-testid={`fu-title-${i}`} />
      <AssigneeSelect personId={personId} assigneeId={r.assignee_id} assigneePersonId={r.assignee_person_id} onChange={set} noneLabel="Me (task creator)" testId={`fu-assignee-${i}`} />
      <Input type="date" value={r.due_date || ""} onChange={(e) => set({ due_date: e.target.value })} data-testid={`fu-due-${i}`} />
      <Button variant="ghost" size="icon" onClick={remove} data-testid={`fu-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}

// Group Admin: turn follow-up items into regular tasks on the agent's record.
function FollowUpTasks({ d, reload }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const done = new Set((d.follow_up_tasks || []).map((t) => t.title));
  const fresh = d.follow_ups.filter((t) => !done.has(t));
  const start = () => setRows((fresh.length ? fresh : [""]).map((t) => ({ title: t })));
  const set = (i, p) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/form-assignments/${d.assignment_id}/follow-up-tasks`, { items: rows });
      toast.success(`${data.created} follow-up task(s) created`); setRows(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!rows) return <Button variant="outline" onClick={start} data-testid="fu-start-btn"><ListChecks className="w-4 h-4 mr-1.5" />Create Follow-Up Tasks</Button>;
  return (
    <div className="w-full rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="fu-panel">
      <p className="font-semibold text-slate-900">Create Follow-Up Tasks</p>
      <p className="text-xs text-slate-500">Tasks are added to {d.overview.agent_name}'s record. Edit, remove or add items, choose who is responsible and a due date.</p>
      {rows.map((r, i) => <TaskRow key={i} r={r} i={i} personId={d.person_id} set={(p) => set(i, p)} remove={() => setRows(rows.filter((_, j) => j !== i))} />)}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={() => setRows([...rows, { title: "" }])} data-testid="fu-add-btn"><Plus className="w-4 h-4 mr-1" />Add item</Button>
        <Button size="sm" className="ml-auto bg-brand hover:bg-brand-strong" disabled={busy || !rows.some((r) => r.title.trim())} onClick={create} data-testid="fu-create-btn">Create {rows.filter((r) => r.title.trim()).length} task(s)</Button>
        <Button size="sm" variant="ghost" onClick={() => setRows(null)}>Cancel</Button>
      </div>
    </div>
  );
}

export default function MeetingPrep() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => { api.get(`/form-assignments/${id}/meeting-prep`).then((r) => setD(r.data)).catch((e) => setErr(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="p-8 text-sm text-rose-600" data-testid="prep-error">{err}</p>;
  if (!d) return <p className="p-8 text-sm text-slate-400">Loading…</p>;
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white py-6 print:py-0" data-testid="meeting-prep-page">
      <style>{"@media print { @page { size: letter; margin: 0.45in; } body * { visibility: hidden !important; } .prep-doc, .prep-doc * { visibility: visible !important; } .prep-doc { position: absolute; left: 0; top: 0; width: 100%; max-width: none; } .prep-sec { break-inside: avoid; } }"}</style>
      <div className="no-print max-w-[8.5in] mx-auto flex flex-wrap items-center justify-end gap-2 mb-3 px-2">
        {d.can_manage && <><MeetingDate key={d.overview.meeting_date || "none"} d={d} reload={load} /><FollowUpTasks d={d} reload={load} /></>}
        <Button onClick={() => window.print()} className="bg-brand hover:bg-brand-strong" data-testid="prep-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print / Save as PDF</Button>
      </div>
      <Doc d={d} />
    </div>
  );
}
